import React, { useRef, useState } from 'react'
import { css } from '@emotion/react'
import { object, func, array, number, bool } from 'prop-types'
import { Loader } from '@livechat/design-system-react-components'

import {
  getImageUrlFromCDN,
  getMarketplaceSettingsLink,
} from '@/utils/products'
import { useTypedSelector } from '@/store/hooks'
import { getUserAvatar } from '@/store/selectors'
import mediaQueries from '@/layout/media-queries'
import LCIcon from '@/assets/icons/logo-livechat-small.inline.svg'
import DeveloperConsoleIcon from '@/assets/icons/developer-console.inline.svg'
import ManageInstallationsIcon from '@/assets/icons/LC-install.inline.svg'
import LogoutIcon from '@/assets/icons/logout.inline.svg'
import avatarPlaceholderPath from '@/assets/images/avatar-placeholder.png'
import PagesMenu from '../PagesMenu'

const avatarCss = (open) => (theme) =>
  css`
    cursor: pointer;
    box-shadow: ${`
    0 0 0 2px ${theme.colors.white},
    0 0 0 4px ${open ? theme.colors.blue700 : theme.colors.primaryAlpha(0)}
  `};

    &,
    > img,
    .loader {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      justify-content: center;
    }

    // Hacky way to prevent text blinking for a split second
    > img {
      color: transparent;
    }
  `

const pagesMenuCss = (leftPosition) => css`
  margin: 0;

  .page-item__dropdown {
    top: 50px;
    left: ${leftPosition ? `${leftPosition}px` : '-140px'};

    ${mediaQueries.xlargeScreen()} {
      left: ${leftPosition ? `${leftPosition}px` : '-120px'};
    }

    ${mediaQueries.xlargeScreen(150)} {
      left: ${leftPosition ? `${leftPosition}px` : '-15px'};
    }
  }
`

const User = ({ menuItems, logOut, leftPosition }) => {
  const userAvatarUrl = useTypedSelector(getUserAvatar)

  const [isLoaded, setIsLoaded] = useState(true)
  const imgRef = useRef()

  const handleError = () => {
    imgRef.current.src = avatarPlaceholderPath
    setIsLoaded(true)
  }

  const handleLoad = () => setIsLoaded(true)
  const handleLoadStart = () => setIsLoaded(false)

  const getUserAvatarImage = ({ isDropdownOpen }) => {
    const avatarUrl = getImageUrlFromCDN(userAvatarUrl)

    return (
      <div css={avatarCss(isDropdownOpen)}>
        {avatarUrl && isLoaded ? (
          <img
            src={avatarUrl}
            alt="User Avatar"
            ref={imgRef}
            onError={handleError}
            onLoad={handleLoad}
            onLoadStart={handleLoadStart}
          />
        ) : (
          <Loader className="loader" />
        )}
      </div>
    )
  }

  const pagesItems = [
    {
      type: 'custom',
      customItem: getUserAvatarImage,
      showDropdownOnClick: true,
      label: 'avatar',
      subItems: menuItems || [
        {
          href: process.env.GATSBY_IFRAME_URL,
          label: 'Open LiveChat App',
          icon: (
            <div>
              <LCIcon
                css={(theme) => `fill: ${theme.colors.cornflowerBlue};`}
              />
            </div>
          ),
        },
        {
          href: process.env.GATSBY_CONSOLE_URL,
          label: 'Developer Console',
          target: '_blank',
          icon: (
            <DeveloperConsoleIcon
              css={(theme) => `fill: ${theme.colors.gray900};`}
            />
          ),
        },
        {
          type: 'divider',
          label: 'first-divider',
        },
        {
          href: getMarketplaceSettingsLink(),
          label: 'My Installed Apps',
          icon: (
            <ManageInstallationsIcon
              css={(theme) => `fill: ${theme.colors.gray900};`}
            />
          ),
        },
        {
          type: 'divider',
          label: 'second-divider',
        },
        {
          href: '',
          icon: <LogoutIcon css={(theme) => `fill: ${theme.colors.error};`} />,
          label: 'Log out',
          onClick: (e) => {
            e.preventDefault()
            logOut()
          },
        },
      ],
    },
  ]

  return (
    <PagesMenu
      data-test="user-menu"
      items={pagesItems}
      css={pagesMenuCss(leftPosition)}
      className="user-page-menu"
    />
  )
}

User.propTypes = {
  user: object,
  logOut: func,
  menuItems: array,
  isDropdownOpen: bool,
  leftPosition: number,
}

export default User
